import React, { useState, useEffect, useRef, useCallback } from "react";
import ChatWindow from "./ChatWindow/ChatWindow";
import Sidebar from "./SideBar/Sidebar";

const useSupportWebSocket = (onTicketNew, onTicketMessage, onTicketUpdate) => {
    const socketRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);

    useEffect(() => {
        let io;
        try {
            io = require("socket.io-client");
        } catch (e) {
            console.warn("socket.io-client не установлен. WebSocket функции будут недоступны.");
            return;
        }

        const connect = () => {
            if (socketRef.current?.connected) return;

            const socket = io("https://api.duotetra.quest/support", {
                withCredentials: true,
                transports: ["websocket"],
                path: "/socket.io",
                reconnection: true,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 5000,
                reconnectionAttempts: Infinity,
            });

            socket.on("connect", () => {
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = null;
                }
            });

            socket.on("connect_error", () => {});

            socket.on("connected", () => {});

            socket.on("ticket:new", (data) => {
                if (onTicketNew) onTicketNew(data.ticket);
            });

            socket.on("ticket:message", (data) => {
                if (onTicketMessage) onTicketMessage(data.ticketId, data.message);
            });

            socket.on("ticket:update", (data) => {
                if (onTicketUpdate) onTicketUpdate(data.ticket);
            });

            socket.on("disconnect", (reason) => {
                if (reason === "io server disconnect") {
                    socket.connect();
                } else if (reason !== "io client disconnect") {
                    reconnectTimeoutRef.current = setTimeout(() => {
                        connect();
                    }, 5000);
                }
            });

            socket.on("error", () => {});

            socketRef.current = socket;
        };

        connect();

        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, [onTicketNew, onTicketMessage, onTicketUpdate]);

    return socketRef.current;
};

const ChatApp = () => {
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [tickets, setTickets] = useState([]);
    const refreshTicketsRef = useRef(null);

    const refreshTickets = useCallback(() => {
        if (refreshTicketsRef.current) {
            refreshTicketsRef.current();
        }
    }, []);

    const handleTicketNew = useCallback((newTicket) => {
        setTickets((prev) => {
            const exists = prev.find((t) => t.id === newTicket.id);
            if (exists) return prev;
            return [newTicket, ...prev];
        });
    }, []);

    const handleTicketMessage = useCallback(
        (ticketId, message) => {
            if (selectedTicket?.id === ticketId) {
                if (window.chatWindowMessageHandler) {
                    window.chatWindowMessageHandler(ticketId, message);
                } else {
                    refreshTickets();
                }
            }
            setTickets((prev) =>
                prev.map((t) => (t.id === ticketId ? { ...t, lastMessageAt: message.createdAt } : t))
            );
        },
        [selectedTicket, refreshTickets]
    );

    const handleTicketUpdate = useCallback(
        (updatedTicket) => {
            setTickets((prev) => prev.map((t) => (t.id === updatedTicket.id ? updatedTicket : t)));
            if (selectedTicket?.id === updatedTicket.id) {
                setSelectedTicket(updatedTicket);
            }
        },
        [selectedTicket]
    );

    useSupportWebSocket(handleTicketNew, handleTicketMessage, handleTicketUpdate);

    return (
        <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-3 p-3 font-poppins text-white md:flex-row md:gap-4 md:p-4">
            <Sidebar
                onSelectChat={setSelectedTicket}
                selectedTicketId={selectedTicket?.id}
                refreshTicketsRef={refreshTicketsRef}
            />
            <ChatWindow chat={selectedTicket} onTicketUpdate={refreshTickets} onNewMessage={handleTicketMessage} />
        </div>
    );
};

export default ChatApp;
